import { useEffect, useState } from "react";
import clsx from "clsx";

// Interfaces
import { ICountDownButtonProps } from "./interfaces";

// MUI
import { Button as MuiButton, Skeleton, Typography, Dialog, DialogContent, DialogActions, Stack } from "@mui/material";
import Button from "@/Layouts/components/Buttons/Button";

const getButtonLabel = (initialCopy = "", actionCopy = "", counterProps: { showCounter?: boolean; counter?: number } | null = null) => {
	const { showCounter = false, counter = 0 } = counterProps ?? {};
	let label = `${initialCopy} ${actionCopy}`.trim();
	if (showCounter) {
		label = `${label} ${counter}`.trim();
	}
	return label;
};

export default function CountDownButton(props: Readonly<ICountDownButtonProps>) {
	const {
		initialCopy: defaultInitialMessage = "Update Now",
		actionCopy: defaultAction = "",
		counter: defaultCounter = 10,
		skeletonSx = null,
		typographySx = null,
		disabled = false,
		loading = true,
		showButton = true,
		confirmBeforeSubmit = false,
		confirmationDialogCopy = "Are you sure you want to proceed?",
		onClickCallBack = () => {},
	} = props;

	const [btnDisabled, setBtnDisabled] = useState(false);
	const [showCounter, setShowCounter] = useState(false);
	const [timer, setTimer] = useState<NodeJS.Timeout | undefined>(undefined);
	const [counter, setCounter] = useState(10);
	const [initialCopy, setInitialCopy] = useState("");
	const [actionCopy, setActionCopy] = useState("");
	const [openDialog, setOpenDialog] = useState(false);

	useEffect(() => {
		setInitialCopy(defaultInitialMessage);
		setActionCopy("");
	}, [props]);

	const handleCountDown = () => {
		if (confirmBeforeSubmit) {
			setOpenDialog(true);
		} else {
			startTimer();
			onClickCallBack?.();
		}
	};

	const startTimer = () => {
		let intTimer = timer;
		let secondsInt = defaultCounter;
		setBtnDisabled(true);
		setShowCounter(true);
		setInitialCopy("");
		setActionCopy(defaultAction ?? defaultInitialMessage);
		setCounter(defaultCounter);

		if (!intTimer && secondsInt > 0) {
			intTimer = setInterval(() => {
				secondsInt = secondsInt - 1;
				setCounter(secondsInt);
				if (secondsInt === 0) {
					setActionCopy("");
					clearInterval(intTimer);
					setBtnDisabled(false);
					setShowCounter(false);
					setCounter(defaultCounter);
					setInitialCopy(defaultInitialMessage);
					setTimer(undefined);
				}
			}, 1000);

			setTimer(intTimer);
		}
	};

	const handleCloseDialog = () => {
		setOpenDialog(false);
	};

	const handleConfirm = () => {
		startTimer();
		onClickCallBack?.();
		handleCloseDialog();
	};

	if (loading) {
		return <Skeleton variant="rectangular" width={100} height={20} sx={{ borderRadius: "20px", ...skeletonSx }} />;
	}

	return (
		<>
			{showButton && (
				<MuiButton
					variant="contained"
					component="label"
					className={clsx({
						"small-action-btn": true,
						"api-call-btn": true,
						"api-call-btn-disabled": btnDisabled || disabled,
					})}
					disabled={btnDisabled || disabled}
					onClick={() => handleCountDown()}
					sx={{
						boxShadow: "none !important",
						"&:hover": {
							color: "white",
							boxShadow: "none !important",
						},
						minHeight: "10px !important",
						height: "15px !important",
					}}
				>
					<Typography sx={{ fontFamily: "var(--font-family-v2)", ...typographySx }}>
						{getButtonLabel(initialCopy, actionCopy, { showCounter, counter })}
					</Typography>
				</MuiButton>
			)}
			<Dialog
				open={openDialog}
				onClose={handleCloseDialog}
				classes={{
					paper: "!rounded-[10px] !shadow-md",
				}}
				disableEscapeKeyDown
				PaperProps={{
					style: {
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						width: "300px",
					},
				}}
			>
				<DialogContent>
					<Typography className={"!font-sans !text-[14.5px] text-default !pt-[5px] max-h-[200px] "}>
						{confirmationDialogCopy}
					</Typography>
				</DialogContent>
				<DialogActions>
					<Stack mx={2} mb={2} direction={"row"} justifyContent={"flex-end"} gap={2}>
						<Button
							onClick={() => handleCloseDialog()}
							variant={"contained"}
							text="Cancel"
							classes="!bg-gray-blue !rounded-md !text-sm !capitalize !font-sans !shadow-none !py-[5px]"
						/>
						<Button
							onClick={handleConfirm}
							variant={"contained"}
							text="Continue"
							classes="!bg-primary !rounded-md !text-sm !capitalize !font-sans !shadow-none !py-[5px]"
						/>
					</Stack>
				</DialogActions>
			</Dialog>
		</>
	);
}
